import React, { useState, useCallback, useEffect } from 'react'
import { useDroppable } from '@dnd-kit/core'
import { useSortable, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { ChevronDown, ChevronRight, GripVertical, Edit, Trash2, Table } from 'lucide-react'
import { ITableComponent, FormComponentType, IFormControlChange, TableCell } from '../df-form-preview-interfaces'
import { v4 as uuidv4 } from 'uuid'

// Import form control components for rendering children
import { DfFormInput } from '../df-form-input'
import { DfFormTextarea } from '../df-form-textarea'
import { DfFormSelect } from '../df-form-select'
import { DfFormCheckbox } from '../df-form-checkbox'
import { DfFormRadio } from '../df-form-radio'
import { DfFormDateTime } from '../df-form-date-time'
import { DfFormSignature } from '../df-form-signature'
import { DfFormHeading } from '../df-form-heading'

// Function to ensure table cell components have proper IDs
const ensureComponentHasId = (component: FormComponentType): FormComponentType => {
  if (!component.id) {
    return {
      ...component,
      id: uuidv4()
    }
  }
  return component
}

interface DfFormTableProps {
  id: string
  properties: ITableComponent
  validationErrors?: Record<string, any>
  formValue?: any
  formData?: Record<string, any>
  readonly?: boolean
  disabled?: boolean
  touchedFields?: Record<string, boolean>
  formSubmitted?: boolean
  mode?: 'edit' | 'preview' | 'test'
  onValueChange?: (change: IFormControlChange) => void
  onBlur?: () => void
  onFocus?: () => void
  onSelect?: () => void
  isSelected?: boolean
  className?: string
  // Table-specific props
  onTableSelect?: (table: ITableComponent) => void
  onTableDelete?: (tableId: string) => void
  onCellChange?: (row: number, column: number, components: FormComponentType[]) => void
  onComponentSelect?: (component: FormComponentType) => void
  onComponentDelete?: (componentId: string) => void
  onComponentEdit?: (component: FormComponentType) => void
  selectedComponent?: FormComponentType | null
  renderFormComponent?: (field: FormComponentType) => React.ReactNode
  // Additional props for consistency with section component
  onTableUpdate?: (tableId: string, updates: Partial<ITableComponent>) => void
  onCellAdd?: (row: number, column: number) => void
  onCellRemove?: (row: number, column: number) => void
  onRowAdd?: () => void
  onRowRemove?: (rowIndex: number) => void
  onColumnAdd?: () => void
  onColumnRemove?: (columnIndex: number) => void
}

// Simple component for preview mode (no drag handles or action buttons)
interface SimpleTableComponentProps {
  component: FormComponentType
  mode: 'edit' | 'preview' | 'test'
  renderFormComponent: (field: FormComponentType) => React.ReactNode
}

const SimpleTableComponent: React.FC<SimpleTableComponentProps> = ({
  component,
  renderFormComponent
}) => {
  return (
    <div className="simple-table-component">
      {renderFormComponent(component)}
    </div>
  )
}

// Draggable component for items inside table cells
interface DraggableTableComponentProps {
  component: FormComponentType
  selectedComponent: FormComponentType | null
  mode: 'edit' | 'preview' | 'test'
  onComponentSelect: (component: FormComponentType) => void
  onComponentDelete: (component: FormComponentType, event: React.MouseEvent) => void
  onComponentEdit?: (component: FormComponentType) => void
  renderFormComponent: (field: FormComponentType) => React.ReactNode
  isOverlay?: boolean
}

const DraggableTableComponent: React.FC<DraggableTableComponentProps> = ({
  component,
  selectedComponent,
  mode,
  onComponentSelect,
  onComponentDelete,
  onComponentEdit,
  renderFormComponent,
  isOverlay = false,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isSorting,
  } = useSortable({
    id: component.id,
    disabled: isOverlay, // Only disable when it's an overlay, allow dragging in edit mode
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
    zIndex: isDragging ? 1000 : 'auto',
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`form-component table-component ${selectedComponent?.id === component.id ? 'selected' : ''} ${isDragging ? 'dragging' : ''} ${isSorting ? 'sorting' : ''}`}
      onClick={() => !isDragging && onComponentSelect(component)}
      role="button"
      tabIndex={0}
    >
      {/* Drag Handle - Only visible in edit mode */}
      {mode === 'edit' && (
        <div 
          className="component-drag-handle" 
          {...listeners} 
          {...attributes}
          onClick={(e) => e.stopPropagation()}
          style={{ opacity: 1 }}
        >
          <GripVertical size={14} />
        </div>
      )}

      {/* Component Content */}
      <div className="component-content">
        {renderFormComponent(component)}
      </div>

      {/* Action Buttons - Only show in edit mode */}
      {mode === 'edit' && (
        <div className="component-actions" style={{ opacity: 1 }}>
          {/* Edit Button */}
          {onComponentEdit && (
            <button
              className="component-edit-btn"
              onClick={(e) => {
                e.stopPropagation()
                onComponentEdit(component)
              }}
              type="button"
              title="Edit Component"
            >
              <Edit size={12} />
            </button>
          )}
          
          {/* Delete Button */}
          <button
            className="component-delete-btn"
            onClick={(e) => {
              e.stopPropagation()
              onComponentDelete(component, e)
            }}
            type="button"
            title="Delete Component"
          >
            <Trash2 size={12} />
          </button>
        </div>
      )}
    </div>
  )
}

// Table Cell Component
interface TableCellComponentProps {
  cell: TableCell
  mode: 'edit' | 'preview' | 'test'
  onComponentSelect: (component: FormComponentType) => void
  onComponentDelete: (component: FormComponentType, event: React.MouseEvent) => void
  onComponentEdit?: (component: FormComponentType) => void
  selectedComponent: FormComponentType | null
  renderFormComponent: (field: FormComponentType) => React.ReactNode
}

const TableCellComponent: React.FC<TableCellComponentProps> = ({
  cell,
  mode,
  onComponentSelect,
  onComponentDelete,
  onComponentEdit,
  selectedComponent,
  renderFormComponent
}) => {
  const dropZoneId = `table-cell-${cell.row}-${cell.column}`
  
  const { setNodeRef, isOver } = useDroppable({
    id: dropZoneId,
    disabled: mode !== 'edit',
    data: {
      type: 'table-cell',
      cell: cell,
      row: cell.row,
      column: cell.column
    }
  })

  // In preview/test mode, show empty cells as transparent spaces to maintain positioning
  // Don't hide them completely to preserve the table structure

  const cellStyle = {
    ...cell.styles,
    backgroundColor: isOver ? 'var(--df-color-primary-light)' : cell.styles?.backgroundColor || 'transparent',
    minHeight: cell.styles?.minHeight || (mode === 'preview' || mode === 'test') ? '40px' : '60px',
    padding: cell.styles?.padding || (mode === 'preview' || mode === 'test') ? '8px' : '8px',
    border: (mode === 'preview' || mode === 'test') ? 'none' : 
            (isOver ? '2px dashed var(--df-color-primary)' : cell.styles?.borderColor ? `1px solid ${cell.styles.borderColor}` : '1px solid var(--df-color-fb-border)'),
    borderRadius: (mode === 'preview' || mode === 'test') ? '0' : '4px',
    position: 'relative' as const,
    verticalAlign: cell.styles?.verticalAlign || 'top'
  }

  return (
    <td
      ref={setNodeRef}
      style={cellStyle}
      className={`table-cell ${isOver ? 'drag-over' : ''}`}
    >
      <div className="cell-content">
        {cell.components.length > 0 ? (
          mode === 'edit' ? (
            <SortableContext items={cell.components.map(c => c.id || uuidv4())} strategy={verticalListSortingStrategy}>
              {cell.components.map((component) => {
                const componentWithId = ensureComponentHasId(component)
                return (
                  <DraggableTableComponent
                    key={componentWithId.id}
                    component={componentWithId}
                    selectedComponent={selectedComponent}
                    mode={mode}
                    onComponentSelect={onComponentSelect}
                    onComponentDelete={onComponentDelete}
                    onComponentEdit={onComponentEdit}
                    renderFormComponent={renderFormComponent}
                  />
                )
              })}
            </SortableContext>
          ) : (
            // In preview/test mode, use simple components without drag handles
            cell.components.map((component) => {
              const componentWithId = ensureComponentHasId(component)
              return (
                <SimpleTableComponent
                  key={componentWithId.id}
                  component={componentWithId}
                  mode={mode}
                  renderFormComponent={renderFormComponent}
                />
              )
            })
          )
        ) : (
          // Only show drop zone content in edit mode
          mode === 'edit' ? (
            <div className="empty-cell-placeholder">
              <div className="cell-info">
                <span className="drop-zone-text">Drag and Drop a form component</span>
                <span className="cell-coordinates">Cell ({cell.row + 1}, {cell.column + 1})</span>
              </div>
            </div>
          ) : (
            // In preview/test mode, show empty cell as transparent space to maintain positioning
            <div style={{ 
              minHeight: '20px', 
              backgroundColor: 'transparent',
              visibility: 'hidden' // Hide content but maintain space
            }}>
              &nbsp;
            </div>
          )
        )}
      </div>
    </td>
  )
}

export const DfFormTable: React.FC<DfFormTableProps> = ({
  id,
  properties,
  mode = 'edit',
  formData = {},
  onValueChange,
  onSelect,
  isSelected = false,
  className = '',
  onTableSelect,
  onComponentSelect,
  onComponentDelete,
  onComponentEdit,
  selectedComponent,
  renderFormComponent
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false) // Always start expanded to show drop zones

  // Check if table has any components in any cells
  const hasAnyComponents = properties.cells?.some(row => 
    row.some(cell => cell.components && cell.components.length > 0)
  ) || false

  // Initialize and update table cells when rows/columns change
  useEffect(() => {
    // Get rows and columns from table properties (where they're actually stored)
    const currentRows = properties.table?.rows || properties.basic?.rows || 3
    const currentColumns = properties.table?.columns || properties.basic?.columns || 3
    const currentCells = properties.cells || []
    
    // Check if we need to update the table structure
    const needsUpdate = currentCells.length === 0 || 
                       currentCells.length !== currentRows || 
                       (currentCells.length > 0 && currentCells[0].length !== currentColumns)
    
    if (needsUpdate) {
      const newCells: TableCell[][] = []
      
      for (let row = 0; row < currentRows; row++) {
        const rowCells: TableCell[] = []
        for (let col = 0; col < currentColumns; col++) {
          const cellId = `cell-${row}-${col}`
          
          // Try to preserve existing components if the cell exists
          let existingComponents: FormComponentType[] = []
          if (currentCells[row] && currentCells[row][col]) {
            existingComponents = (currentCells[row][col].components || []).map(ensureComponentHasId)
          }
          
          rowCells.push({
            id: cellId,
            row,
            column: col,
            components: existingComponents,
            styles: {}
          })
        }
        newCells.push(rowCells)
      }
      
      if (onValueChange) {
        onValueChange({
          id,
          value: { ...properties, cells: newCells }
        })
      }
    }
  }, [properties.table?.rows, properties.table?.columns, properties.basic?.rows, properties.basic?.columns, properties.cells, id, onValueChange]) // Watch for rows/columns changes

  const handleTableClick = useCallback((event: React.MouseEvent) => {
    event.stopPropagation()
    onSelect?.()
    onTableSelect?.(properties)
  }, [onSelect, onTableSelect, properties])

  const toggleCollapse = (event: React.MouseEvent) => {
    event.stopPropagation()
    setIsCollapsed(!isCollapsed)
    onValueChange?.({
      id,
      value: { ...properties, basic: { ...(properties.basic || {}), collapsed: !isCollapsed } }
    })
  }


  const handleComponentDelete = useCallback((component: FormComponentType, event: React.MouseEvent) => {
    event.stopPropagation()

    // Find and remove the component from the table cell
    const updatedCells = properties.cells.map(row => 
      row.map(cell => {
        if (cell.components && cell.components.some(comp => comp.id === component.id)) {  
          const filteredComponents = cell.components.filter(comp => comp.id !== component.id)
          
          return {
            ...cell,
            components: filteredComponents
          }
        }
        return cell
      })
    )
    
    // Update the table with the modified cells
    if (onValueChange) {
      onValueChange({
        id,
        value: { ...properties, cells: updatedCells }
      })
    } else {
    }
    
    // Don't call parent delete handler for table cell components
    // The parent delete handler is for deleting entire fields, not components within table cells
  }, [onComponentDelete, properties, onValueChange, id])

  // CRITICAL FIX: Ensure all table cell components have proper IDs
  const ensureTableCellComponentsHaveIds = (cells: any[][]) => {
    return cells.map((row: any[]) => 
      row.map((cell: any) => ({
        ...cell,
        components: cell.components?.map((comp: any) => {
          if (!comp.id) {
            return {
              ...comp,
              id: `table-cell-${comp.name}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
            }
          }
          return comp
        }) || []
      }))
    )
  }
  
  // Apply the fix to ensure all table cell components have IDs
  const cellsWithIds = properties.cells ? ensureTableCellComponentsHaveIds(properties.cells) : []
  
  // CRITICAL FIX: Update the parent component with the cells that have proper IDs
  useEffect(() => {
    if (cellsWithIds.length > 0 && JSON.stringify(cellsWithIds) !== JSON.stringify(properties.cells)) {
      // Notify parent component that cells have been updated with proper IDs
      onValueChange?.({
        id: id,
        value: {
          ...properties,
          cells: cellsWithIds
        }
      })
    }
  }, [cellsWithIds, properties.cells, id, onValueChange])
  
  const renderComponent = renderFormComponent || useCallback((field: FormComponentType) => {
    // Ensure the component has a proper ID
    const componentWithId = ensureComponentHasId(field)
    
    const commonProps = {
      id: componentWithId.id,
      properties: componentWithId,
      validationErrors: {},
      formValue: formData[componentWithId.id] || ('defaultValue' in componentWithId.basic ? componentWithId.basic.defaultValue || '' : ''),
      touchedFields: {},
      formSubmitted: false,
      mode,
      onValueChange: (change: IFormControlChange) => {
        onValueChange?.(change)
      },
      onBlur: () => {},
      onFocus: () => {}
    }

    switch (componentWithId.name) {
      case 'text-input':
      case 'email-input':
      case 'number-input':
        return (
          <DfFormInput
            {...commonProps}
            properties={componentWithId as any}
            inputType={componentWithId.name === 'text-input' ? 'text' : 
                      componentWithId.name === 'number-input' ? 'number' : 'email'}
            readonly={mode === 'edit' || mode === 'preview'}
            disabled={mode === 'preview'}
          />
        )
      
      case 'textarea':
        return <DfFormTextarea {...commonProps} properties={componentWithId as any} readonly={mode === 'edit' || mode === 'preview'} disabled={mode === 'preview'} />
      
      case 'select':
        // Map basic.options to top-level options for select component
        const selectProps = {
          ...componentWithId,
          options: (componentWithId as any).basic?.options || []
        }
        return <DfFormSelect {...commonProps} properties={selectProps as any} disabled={mode === 'edit' || mode === 'preview'} />
      
      case 'checkbox':
        // Map basic.options to top-level options for checkbox component
        const checkboxProps = {
          ...componentWithId,
          options: (componentWithId as any).basic?.options || []
        }
        return <DfFormCheckbox {...commonProps} properties={checkboxProps as any} formValue={[]} disabled={mode === 'edit' || mode === 'preview'} />
      
      case 'radio':
        // Map basic.options to top-level options for radio component
        const radioProps = {
          ...componentWithId,
          options: (componentWithId as any).basic?.options || []
        }
        return <DfFormRadio {...commonProps} properties={radioProps as any} readonly={mode === 'edit' || mode === 'preview'} disabled={mode === 'edit' || mode === 'preview'} />
      
      case 'date':
      case 'date-picker':
        return <DfFormDateTime {...commonProps} properties={componentWithId as any} readonly={mode === 'edit' || mode === 'preview'} disabled={mode === 'preview'} />
      
      case 'datetime-picker':
        return <DfFormDateTime {...commonProps} properties={componentWithId as any} readonly={mode === 'edit' || mode === 'preview'} disabled={mode === 'preview'} />
      
      case 'signature':
        return <DfFormSignature {...commonProps} properties={componentWithId as any} />
      
      case 'heading':
        return <DfFormHeading {...commonProps} properties={componentWithId as any} />
      
      default:
        return <div className="unknown-component">Unknown component: {componentWithId.name}</div>
    }
  }, [mode])

  const tableStyle = {
    backgroundColor: properties.styles?.backgroundColor || 'var(--df-color-fb-container)',
    borderColor: properties.styles?.borderColor || 'var(--df-color-fb-border)',
    borderWidth: properties.styles?.borderWidth || '2px',
    borderRadius: properties.styles?.borderRadius || '8px',
    padding: properties.styles?.padding || '16px',
    margin: properties.styles?.margin || '16px 0'
  }

  const tableElementStyle: React.CSSProperties = {
    width: '100%',
    minWidth: '600px', // Ensure minimum width to trigger horizontal scroll
    borderCollapse: (mode === 'preview' || mode === 'test') ? 'separate' : 'collapse',
    tableLayout: 'fixed',
    border: (mode === 'preview' || mode === 'test') ? '1px solid var(--df-color-fb-border)' : 'none'
  }

  // Don't render the table if it has no components and we're in preview mode
  // Test mode should always show tables so users can interact with them
  if (!hasAnyComponents && mode === 'preview') {
    return null
  }


  return (
    <div
      className={`form-group df-form-table ${isSelected ? 'selected' : ''} ${mode === 'preview' ? 'preview-mode' : mode === 'test' ? 'test-mode' : ''} ${className}`}
      onClick={handleTableClick}
      style={tableStyle}
    >
      {/* Table Header */}
      <div className="table-header">
        <div className="table-title" onClick={toggleCollapse}>
          <div className="title-content">
            {isCollapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
            <Table size={16} />
            <span className="table-label">{properties.basic?.label || 'Table'}</span>
            {properties.validation?.required && (
              <span className="required-indicator">*</span>
            )}
          </div>
        </div>
        
       
      </div>

      {/* Table Description */}
      {properties.basic?.description && !isCollapsed && (
        <div className="table-description">
          {properties.basic.description}
        </div>
      )}

      {/* Table Content */}
      {!isCollapsed && (
        <div className="table-content">
          {(() => {
            return null
          })()}
          <table style={tableElementStyle}>
            {/* Column Headers */}
            {properties.table?.displayAsTable && (mode === 'edit' || properties.table?.showColumns) && (
              <thead>
                <tr className="table-header-row">
                  {Array.from({ length: properties.table?.columns || properties.basic?.columns || 3 }, (_, colIndex) => {
                    // Parse column names from the comma-separated string
                    const columnNames = properties.table?.columnNames?.split(',').map(name => name.trim()) || []
                    const columnName = columnNames[colIndex] || `Column ${colIndex + 1}`
                    
                    return (
                      <th 
                        key={`header-${colIndex}`}
                        className="table-header-cell"
                        style={{
                          backgroundColor: properties.styles.headerBackgroundColor || (mode === 'preview' || mode === 'test') ? 'var(--df-color-fb-container)' : 'var(--df-color-fb-container)',
                          color: properties.styles.headerTextColor || 'var(--df-color-text-dark)',
                          padding: '12px',
                          border: (mode === 'preview' || mode === 'test') ? '1px solid var(--df-color-fb-border)' : '1px solid var(--df-color-fb-border)',
                          fontWeight: '600',
                          fontSize: '14px',
                          textAlign: 'center'
                        }}
                      >
                        {columnName}
                      </th>
                    )
                  })}
                </tr>
              </thead>
            )}
            <tbody>
              {properties.cells?.map((row, rowIndex) => (
                <tr 
                  key={rowIndex}
                  className="table-row"
                >
                  {row.map((cell) => (
                    <TableCellComponent
                      key={cell.id}
                      cell={cell}
                      mode={mode}
                      onComponentSelect={onComponentSelect || (() => {})}
                      onComponentDelete={handleComponentDelete}
                      onComponentEdit={onComponentEdit}
                      selectedComponent={selectedComponent || null}
                      renderFormComponent={renderComponent}
                    />
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default DfFormTable